import type {ReactElement} from 'react';
import {useId, useState} from 'react';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text/text';
import {WebSourceFavicon} from './components/web-source-favicon/web-source-favicon';
import {WebSourceRow} from './components/web-source-row/web-source-row';
import {getSearchedSourcesLabel} from './web-sources.library';
import type {WebSource, WebSourcesProps} from './web-sources.types';
import {MAX_STACKED_FAVICONS} from './web-sources.types';
import styles from './web-sources.module.scss';

/** One quiet line under the reply — how many sites were searched and their favicons — that expands into the numbered list of pages. */
export function WebSources({sources}: WebSourcesProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false);
  const sourceListId = useId();

  const toggleSourceList = (): void => {
    setIsExpanded((wasExpanded) => !wasExpanded);
  };

  return (
    <div className={styles.webSources}>
      <button type="button" className={styles.webSourcesToggle} aria-expanded={isExpanded} aria-controls={sourceListId} onClick={toggleSourceList}>
        <Icon icon="icon-search" size={IconSize.SIZE_ICON_16} className={styles.webSourcesToggleIcon} />
        <Text val={getSearchedSourcesLabel(sources.length)} size={TextSize.SM} className={styles.webSourcesLabel} />
        <span className={styles.webSourcesFavicons} aria-hidden="true">
          {sources.slice(0, MAX_STACKED_FAVICONS).map((source) => (
            <WebSourceFavicon key={source.url} domain={source.domain} className={styles.webSourcesStackedFavicon} />
          ))}
          {renderOverflowCount(sources)}
        </span>
        <Icon icon="icon-caret-down" size={IconSize.SIZE_ICON_16} className={isExpanded ? styles.webSourcesCaretExpanded : styles.webSourcesCaret} />
      </button>
      <ol id={sourceListId} className={styles.webSourceList} aria-label={window.i18next.t('pmwjs_ai_chat_web_sources_title')} hidden={!isExpanded}>
        {sources.map((source, index) => (
          <WebSourceRow key={source.url} source={source} position={index + 1} />
        ))}
      </ol>
    </div>
  );
}

function renderOverflowCount(sources: WebSource[]): ReactElement | null {
  if (sources.length <= MAX_STACKED_FAVICONS) {
    return null;
  }

  return <Text val={`+${sources.length - MAX_STACKED_FAVICONS}`} size={TextSize.SM} bold className={styles.webSourcesOverflowCount} />;
}
